import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useTranslation } from "@/context/TranslationContext";

const plans = [
  {
    name: "Starter",
    taglineKey: "pricing.starter_tagline",
    price: { monthly: "100,000 - 250,000", annual: "100,000 - 250,000" },
    periodKey: "pricing.starter_period",
    descriptionKey: "pricing.starter_description",
    featureKeys: [
      "pricing.feature_starter_pages",
      "pricing.feature_starter_auth",
      "pricing.feature_starter_api",
      "pricing.feature_starter_responsive",
      "pricing.feature_starter_support",
      "pricing.feature_starter_source",
      "pricing.feature_starter_stripe",
      "pricing.feature_starter_admin",
      "pricing.feature_starter_realtime",
      "pricing.feature_starter_cicd",
    ],
    included: [true, true, true, true, true, true, false, false, false, false],
    ctaKey: "pricing.starter_cta",
    highlighted: false,
    color: "border-dark-border",
    badge: null,
  },
  {
    name: "Growth",
    taglineKey: "pricing.growth_tagline",
    price: { monthly: "300,000 - 500,000", annual: "300,000 - 500,000" },
    periodKey: "pricing.starter_period",
    descriptionKey: "pricing.growth_description",
    featureKeys: [
      "pricing.feature_growth_pages",
      "pricing.feature_growth_auth",
      "pricing.feature_growth_api",
      "pricing.feature_growth_stripe",
      "pricing.feature_growth_admin",
      "pricing.feature_growth_realtime",
      "pricing.feature_growth_support",
      "pricing.feature_growth_cicd",
      "pricing.feature_growth_uploads",
      "pricing.feature_growth_email",
    ],
    included: [true, true, true, true, true, true, true, true, true, true],
    ctaKey: "pricing.growth_cta",
    highlighted: true,
    color: "border-electric-blue/40",
    badgeKey: "pricing.most_popular",
  },
  {
    name: "Enterprise",
    taglineKey: "pricing.enterprise_tagline",
    price: { monthly: "Custom", annual: "Custom" },
    periodKey: null,
    descriptionKey: "pricing.enterprise_description",
    featureKeys: [
      "pricing.feature_enterprise_architecture",
      "pricing.feature_enterprise_team",
      "pricing.feature_enterprise_systems",
      "pricing.feature_enterprise_sla",
      "pricing.feature_enterprise_support",
      "pricing.feature_enterprise_audit",
      "pricing.feature_enterprise_retainer",
      "pricing.feature_enterprise_whitelabel",
      "pricing.feature_enterprise_priority",
      "pricing.feature_enterprise_manager",
    ],
    included: [true, true, true, true, true, true, true, true, true, true],
    ctaKey: "pricing.enterprise_cta",
    highlighted: false,
    color: "border-dark-border",
    badge: null,
  },
];

const faqs = [
  {
    qKey: "pricing.faq1_q",
    aKey: "pricing.faq1_a",
  },
  {
    qKey: "pricing.faq2_q",
    aKey: "pricing.faq2_a",
  },
  {
    qKey: "pricing.faq3_q",
    aKey: "pricing.faq3_a",
  },
  {
    qKey: "pricing.faq4_q",
    aKey: "pricing.faq4_a",
  },
  {
    qKey: "pricing.faq5_q",
    aKey: "pricing.faq5_a",
  },
  {
    qKey: "pricing.faq6_q",
    aKey: "pricing.faq6_a",
  },
];

export default function Pricing() {
  const { t } = useTranslation();
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("monthly");
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-deep-black font-inter">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-36 pb-16 px-4 md:px-12 lg:px-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute top-1/4 -right-20 w-96 h-96 bg-electric-blue/5 rounded-full blur-[120px]" />
          <div className="absolute bottom-1/4 -left-20 w-80 h-80 bg-cyber-blue/5 rounded-full blur-[100px]" />
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-3xl">
            <span className="inline-block text-electric-blue text-xs font-semibold uppercase tracking-[0.2em] mb-5 px-3 py-1.5 border border-electric-blue/25 rounded-full bg-electric-blue/5">
              {t("pricing.hero_badge")}
            </span>
            <h1 className="font-black text-white leading-tight mb-5" style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}>
              {t("pricing.hero_title")} <span className="text-transparent bg-clip-text bg-gradient-to-r from-electric-blue to-cyber-blue">{t("pricing.hero_highlight")}</span>
            </h1>
            <p className="text-silver text-base leading-relaxed mb-8 max-w-xl">
              {t("pricing.hero_description")}
            </p>

            {/* Billing Toggle */}
            <div className="inline-flex items-center gap-1 p-1 bg-dark-card border border-dark-border rounded-full">
              <button
                onClick={() => setBillingCycle("monthly")}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  billingCycle === "monthly"
                    ? "bg-electric-blue text-deep-black"
                    : "text-silver hover:text-white"
                }`}
              >
                {t("pricing.billing_standard")}
              </button>
              <button
                onClick={() => setBillingCycle("annual")}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center gap-2 ${
                  billingCycle === "annual"
                    ? "bg-electric-blue text-deep-black"
                    : "text-silver hover:text-white"
                }`}
              >
                {t("pricing.billing_bulk")}
                <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${billingCycle === "annual" ? "bg-deep-black/20 text-deep-black" : "bg-cyber-green/10 text-cyber-green border border-cyber-green/30"}`}>
                  {t("pricing.billing_save")}
                </span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="pb-20 px-4 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-5">
          {plans.map((plan) => (
            <div
              key={plan.name}
              className={`relative rounded-2xl border p-8 transition-all duration-300 ${plan.color} ${
                plan.highlighted
                  ? "bg-gradient-to-b from-electric-blue/8 to-dark-card"
                  : "bg-dark-card hover:border-electric-blue/15"
              }`}
            >
              {plan.badgeKey && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <span className="bg-electric-blue text-deep-black text-xs font-black px-4 py-1.5 rounded-full whitespace-nowrap tracking-wide">
                    {t(plan.badgeKey)}
                  </span>
                </div>
              )}

              {/* Plan Header */}
              <div className="mb-7">
                <h2 className="text-white font-black text-xl mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {plan.name}
                </h2>
                <p className="text-electric-blue text-xs font-semibold mb-3 uppercase tracking-widest">{t(plan.taglineKey)}</p>
                <p className="text-silver-dim text-sm leading-relaxed mb-5">{t(plan.descriptionKey)}</p>
                <div className="flex items-baseline gap-1.5">
                  <span
                    className={`text-5xl font-black ${plan.highlighted ? "text-electric-blue" : "text-white"}`}
                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                  >
                    {plan.price[billingCycle]}
                  </span>
                  {plan.periodKey && (
                    <span className="text-silver-dim text-sm">{t(plan.periodKey)}</span>
                  )}
                </div>
                {billingCycle === "annual" && plan.name !== "Enterprise" && (
                  <p className="text-cyber-green text-xs mt-1.5 flex items-center gap-1">
                    <i className="ri-arrow-down-line" /> {t("pricing.billing_save")} {t("pricing.bulk_retainer")}
                  </p>
                )}
              </div>

              {/* Features */}
              <ul className="space-y-3 mb-8">
                {plan.featureKeys.map((featureKey, index) => (
                  <li key={featureKey} className="flex items-center gap-3 text-sm">
                    <div className="w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0 bg-electric-blue/10 border border-electric-blue/20">
                      {plan.included[index] ? (
                        <i className={`ri-check-line text-xs ${plan.highlighted ? "text-electric-blue" : "text-cyber-green"}`} />
                      ) : (
                        <i className="ri-close-line text-xs text-silver-dim/40" />
                      )}
                    </div>
                    <span className={plan.included[index] ? "text-silver" : "text-silver-dim/40"}>
                      {t(featureKey)}
                    </span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Link
                to="/contact"
                className={`flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold text-sm transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  plan.highlighted
                    ? "bg-electric-blue text-deep-black hover:shadow-glow-blue"
                    : "border border-dark-border text-silver hover:border-electric-blue/30 hover:text-white"
                }`}
              >
                {t(plan.ctaKey)}
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 px-4 md:px-12 lg:px-20 bg-dark-panel border-t border-dark-border">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black text-white text-center mb-10" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {t("pricing.comparison_title")}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-dark-border">
                  <th className="text-left text-silver-dim font-medium py-3 pr-6 w-1/2">Feature</th>
                  <th className="text-center text-silver font-semibold py-3 px-4">Starter</th>
                  <th className="text-center text-electric-blue font-semibold py-3 px-4">Growth</th>
                  <th className="text-center text-silver font-semibold py-3 px-4">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {[
                  [t("pricing.feature_auth"), "Basic", "Full RBAC", "Custom SSO"],
                  [t("pricing.feature_api"), "REST", "REST + WS", "Custom"],
                  [t("pricing.feature_database"), "PostgreSQL", "PostgreSQL + Redis", "Custom"],
                  [t("pricing.feature_payment"), "—", "Stripe", "Multi-gateway"],
                  [t("pricing.feature_dashboard"), "—", "Included", "Custom"],
                  [t("pricing.feature_realtime"), "—", "Socket.io", "Custom"],
                  [t("pricing.feature_uploads"), "—", "Included", "CDN + S3"],
                  [t("pricing.feature_email"), "—", "Included", "Custom"],
                  [t("pricing.feature_cicd"), "—", "Included", "Enterprise CI"],
                  [t("pricing.feature_support"), "1 month", "3 months", "12 months"],
                  [t("pricing.feature_sla"), "—", "—", "99.9% uptime"],
                  [t("pricing.feature_audit"), "—", "—", "Included"],
                ].map(([feature, starter, growth, enterprise]) => (
                  <tr key={feature} className="border-b border-dark-border/50 hover:bg-dark-card/30 transition-colors">
                    <td className="py-3.5 pr-6 text-silver">{feature}</td>
                    <td className="py-3.5 px-4 text-center text-silver-dim">{starter}</td>
                    <td className="py-3.5 px-4 text-center text-electric-blue font-medium">{growth}</td>
                    <td className="py-3.5 px-4 text-center text-silver-dim">{enterprise}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 md:px-12 lg:px-20">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block text-electric-blue text-xs font-semibold uppercase tracking-[0.2em] mb-4 px-3 py-1.5 border border-electric-blue/25 rounded-full bg-electric-blue/5">
              {t("pricing.faq_badge")}
            </span>
            <h2 className="text-3xl font-black text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              {t("pricing.faq_title")}
            </h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-dark-card border border-dark-border rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer hover:bg-dark-card/80 transition-colors"
                >
                  <span className="text-white font-medium text-sm pr-4">{t(faq.qKey)}</span>
                  <div className="w-6 h-6 flex items-center justify-center rounded-full border border-dark-border flex-shrink-0">
                    <i className={`ri-${openFaq === index ? "subtract" : "add"}-line text-electric-blue text-xs`} />
                  </div>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5 border-t border-dark-border/50">
                    <p className="text-silver text-sm leading-relaxed pt-4">{t(faq.aKey)}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 px-4 md:px-12 lg:px-20 bg-dark-panel border-t border-dark-border">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            {t("pricing.cta_title")}
          </h2>
          <p className="text-silver text-base mb-8 leading-relaxed">
            {t("pricing.cta_description")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="flex items-center gap-2 bg-electric-blue text-deep-black font-bold px-8 py-3.5 rounded-full hover:shadow-glow-blue transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {t("pricing.cta_primary")}
              <i className="ri-calendar-line" />
            </Link>
            <Link
              to="/portfolio"
              className="flex items-center gap-2 border border-dark-border text-silver hover:border-electric-blue/30 hover:text-white px-8 py-3.5 rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {t("pricing.cta_secondary")}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
